import {useRouter} from '@/router';
import {useParams} from '@/router/router';
import {RouterAliases} from '@/router/routerAliases';
import {
  getAvailableTripThemes,
  getDefaultImages,
  getDisplayFlights,
  getDisplayHotels,
  getSelectedTheme,
  getSolutionContexts,
  getSolutionCost,
  getSolutionImg,
  getTripId,
  getTripName,
  getDiningActivities,
  getExperienceActivities,
  removePOIFromSolution,
  toggleFlightOrStay,
  updateSolutionImg,
  updateSolutionName,
  getAllRecommendationsContent,
  getPoiAlternatives,
} from '@/store/state/helpers';
import {formatDate, getListOfSolutionDays, isFlightOverdue, isFlightsAvailable} from '@/store/state/utils';
import {formatBudget, pluralFormatter} from '@mobi-components/amex-bluesky-components';
import {
  Activity,
  CandidatePoiItem,
  CandidateTransportationItem,
  CurrencyCode,
  getReadableDuration,
  getSolutionDates,
  getSolutionDatesRange,
  IDateDescription,
  Price,
  UserActivityCategoryEnum,
} from '@mobi-components/global-solution';
import {computed, ref} from '@vue/composition-api';
import {useSolutionFilters, VueWithModal} from './summary-filters';
import {RoutingCategory, useDetailsHeader} from './details-header-handler';
import {useStay} from './stay';
import {AdditionalPoisModule} from '@/store/modules';

interface DateRange {
  startDate: number;
  endDate: number;
}

export function useSolutionViews(root: VueWithModal) {
  const removedItems = ref<Record<string, boolean>>({});
  const {timeFrameFilter, onFilterClick, travelersFilter, availableSolutionsInterestTags} = useSolutionFilters(root);
  const {solution, stayActivity, allStayActivities} = useStay();
  const toggleFlightsState = ref(false);

  const availableTripOptions = computed(() => {
    return getAvailableTripThemes();
  });

  const isOneLocation = computed<boolean>(() => {
    // @ts-ignore
    return availableTripOptions.value.some((x) => x.flight_optional);
  });

  const {replaceCategory, ...restOfHeader} = useDetailsHeader(isOneLocation);

  const isFlightAvailable = computed<boolean>(() => {
    return isFlightsAvailable(flights.value);
  });

  const flightExplanations = computed(() => {
    const flight = flights.value.find(Boolean);
    return flight?.explanations ? flight.explanations.join(', ') : '';
  });

  const isFlightInThePast = computed<boolean>(() => {
    return isFlightOverdue(flights.value);
  });

  const solutionImg = computed(() => {
    return getSolutionImg();
  });

  const isLoading = computed(() => {
    return !solution.value || toggleFlightsState.value;
  });

  const destinationName = computed(() => {
    const params = useParams();
    return params.value.contextName?.replace('_', ', ');
  });

  const pdfDestinationName = computed(() => {
    return getTripName().replace(/_/g, ', ');
  });

  const solutionCost = computed(() => {
    return getSolutionCost();
  });

  const price = computed<readonly Price[]>(() => {
    return solutionCost.value?.length ? solutionCost.value : [];
  });

  const currency = computed(() => {
    return solutionCost.value?.length ? solutionCost.value[0].currency : CurrencyCode.USD;
  });

  const onRemoveFromTheList = async (activity: Activity) => {
    removedItems.value[activity?.id || ''] = true;
    removedItems.value = {...removedItems.value};
    await removePOIFromSolution(activity?.id || '');
    removedItems.value = {};
    const params = useParams();
    const {poiId} = params.value;
    if (poiId === activity.id || poiId === activity.source?.candidate_id) {
      replaceCategory({cat: activity.category as RoutingCategory});
    }
  };

  const backToSummaryView = () => {
    const router = useRouter();
    router.push({name: RouterAliases.SUMMARY_VIEW});
  };

  const openHighlightDetails = (sourceId: string) => {
    if (!sourceId) {
      console.warn('openHighlightDetails(): sourceId is falsie', sourceId);
      return;
    }

    const activity = solution.value?.activities.find((x) => x.id === sourceId);
    const router = useRouter();
    router.push({name: RouterAliases.POI_DETAILS_VIEW, params: {poiId: activity?.source?.content?.id || sourceId}});
  };

  const openRecommendations = (id = '') => {
    const router = useRouter();
    if (router.currentRoute.name === RouterAliases.SUMMARY_VIEW) {
      router.push({
        name: RouterAliases.EXPERIENCE_EXPLORE_VIEW,
        query: {showRecommendations: 'true', activityId: id || ''},
      });
    }

    const activity = solution.value?.activities.find((x) => x.id === id);
    if (activity?.source?.candidate_id) {
      root.$emit('select-recommendation', activity?.source?.candidate_id);
    }
  };

  const onShowPoiDetailsFromMap = (poiId: string) => {
    // const activity = solution.value?.activities.find((x) => x.source?.content?.id === poiId || x.id === poiId);
    // if (activity?.spec?.curated && activity.category !== UserActivityCategoryEnum.ACCOMMODATION) {
    //   return openRecommendations(activity.id);
    // }
    // if (activity) {
    //   return openHighlightDetails(activity.id);
    // }

    const router = useRouter();
    router.push({name: RouterAliases.POI_DETAILS_VIEW, params: {poiId}});
  };

  const solutionTimeFrameFilterValue = computed(() => {
    const days = getSolutionDatesRange(solution.value);
    if (!days) {
      return '';
    }
    return getReadableDuration(days.startDate, days.endDate);
  });

  const solutionDatesRange = computed<IDateDescription[]>(() => {
    if (!solution.value) {
      return [];
    }
    const params = useParams();
    const context = params.value.contextName;
    const days = getListOfSolutionDays(solution.value, context);
    return (getSolutionDates(days) || []).map((x, index) => ({
      ...x,
      label: `Day ${index + 1}`,
      description: formatDate(x.date),
    }));
  });

  const flights = computed(() => {
    const params = useParams();
    const context = params.value.contextName;
    return (
      solution.value?.activities.filter(
        (x) =>
          x.category === UserActivityCategoryEnum.FLIGHT && (!context || x.source.content?.contexts.includes(context)),
      ) || []
    );
  });

  const activeTheme = computed(() => {
    return getSelectedTheme();
  });

  const selectedTripOption = computed(() => {
    return availableTripOptions.value.find((x) => x.theme === activeTheme.value);
  });

  const flightsImg = computed(() => {
    const {flightImg} = getDefaultImages();
    const isEstimated = !isFlightAvailable.value;
    if (isEstimated || isOneLocation.value || !isFlightsOn.value) {
      return flightImg;
    }
    if (!flights.value.length) {
      return (selectedTripOption.value?.flights || [])
        .map((x) => (x.images || {}).small || '')
        .concat(flightImg)
        .slice(0, 2);
    }
    return flights.value
      .slice(0, 2)
      .map((x) => (x.images || {}).small || '')
      .concat(flightImg)
      .slice(0, 2);
  });

  const getFlightName = (flights: CandidateTransportationItem[]) => {
    return (flights || [])
      .map((x) => {
        return [x.origin?.name?.replace(/_/gi, ', '), x.destination?.name?.replace(/_/gi, ', ')].filter(Boolean);
      })
      .flat()
      .filter((_, index) => index === 0 || index === 1)
      .join(' to ');
  };

  const flightDescr = computed(() => {
    return getFlightName(
      (flights.value || [])
        .map((x) => {
          return (x.source?.content as CandidateTransportationItem) || [];
        })
        .flat(),
    );
  });

  const flightCaption = computed(() => {
    if (!isFlightsOn.value) {
      return 'Turned Off';
    }
    if (!isFlightAvailable.value) {
      return 'No Flights Available';
    }
    return flightDescr.value ? `${flightDescr.value}` : 'Choose Best';
  });
  const flightPriceDescription = computed(() => {
    if (!isFlightAvailable.value || !isFlightsOn.value) {
      return '';
    }
    const travelersFilterValue = travelersFilter.adults + travelersFilter.children;
    const plural = pluralFormatter(travelersFilterValue, 'Traveler', 'Travelers');
    const numberOfAdults = travelersFilterValue ? `(${travelersFilterValue} ${plural})` : '';
    return `Roundtrip ${numberOfAdults}`;
  });

  const flightPrice = computed(() => {
    if (!isFlightAvailable.value || !isFlightsOn.value) {
      return '';
    }
    // @ts-ignore
    const flightWithGroup = flights.value.find((x: Activity) => x.group_id);
    // @ts-ignore
    const group = (solution.value?.activity_groups || []).find((x) => x.id === flightWithGroup.group_id);
    const groupPrice: Price[] = group?.source?.content?.prices;

    const allPrices = groupPrice || (flights.value || []).map((x) => x.price).filter(Boolean);
    const fPrice = allPrices.reduce(
      (sum, x) => {
        if (x.currency === CurrencyCode.USD) {
          return {
            amount: sum.amount + x.amount,
            currency: CurrencyCode.USD,
          };
        }
        return sum;
      },
      {amount: 0, currency: CurrencyCode.USD},
    );
    return formatBudget(fPrice.amount, CurrencyCode.USD);
  });

  /*
  // function is not used
  const _allAccomodations = computed(() => {
    if (!solution.value) {
      return [];
    }

    return solution.value.activities.filter((x) => {
      return (
        !x.mode && // not transportation activites
        x.category &&
        x.category === UserActivityCategoryEnum.ACCOMMODATION // not Stay activities
      );
    });
  });
*/

  const stayName = computed(() => {
    return stayActivity.value?.name || '';
  });

  const getActivityPrice = (activity?: Activity) => {
    if (!activity) {
      return '';
    }
    const prices = activity.prices;
    let sPrice = activity.price;
    if (prices?.length) {
      sPrice = prices[0];
    }
    return sPrice ? formatBudget(sPrice.amount, sPrice.currency) : '';
  };

  const stayPrice = computed(() => {
    return getActivityPrice(stayActivity.value);
  });

  const numberActivitiesInTheSolution = computed(() => {
    const params = useParams();
    const context = params.value.contextName;
    return new Set(
      (solution.value?.activities || [])
        .filter(
          (x) =>
            // @ts-ignore
            x.spec?.context === context &&
            x.category !== UserActivityCategoryEnum.ACCOMMODATION &&
            x.category !== UserActivityCategoryEnum.FLIGHT,
        )
        .map((x) => x.source?.content?.id)
        .filter(Boolean) || [],
    );
  });

  const experincesCaption = computed(() => {
    return numberActivitiesInTheSolution.value.size ? `<b>${numberActivitiesInTheSolution.value.size}</b> Places` : '';
  });

  const turnOffFlight = async () => {
    const newTripId = getTripId();
    const displayFlights = getDisplayFlights();
    const displayHotels = getDisplayHotels();
    toggleFlightsState.value = true;
    await toggleFlightOrStay(newTripId, !displayFlights, displayHotels);
    toggleFlightsState.value = false;
  };

  const turnOffStay = async () => {
    const newTripId = getTripId();
    const displayFlights = getDisplayFlights();
    const displayHotels = getDisplayHotels();
    toggleFlightsState.value = true;
    await toggleFlightOrStay(newTripId, displayFlights, !displayHotels);
    toggleFlightsState.value = false;
  };

  const isFlightsOn = computed(() => {
    return getDisplayFlights();
  });
  const isStaysOn = computed(() => {
    return getDisplayHotels();
  });

  const availableContexts = computed(() => {
    const params = useParams();
    const contextDates =
      solution.value?.activities.reduce((result: Record<string, DateRange>, x) => {
        // @ts-ignore
        const context = x.spec?.context || '';
        result[context] = result[context] || {startDate: 0, endDate: 0};

        if (!result[context].startDate || result[context].startDate > x.scheduled_start_time) {
          result[context].startDate = x.scheduled_start_time;
        }
        if (!result[context].endDate || result[context].endDate < x.scheduled_end_time) {
          result[context].endDate = x.scheduled_end_time;
        }
        return result;
      }, {}) || {};

    const notDuplicatedContexts = getSolutionContexts();
    return notDuplicatedContexts.map((x) => {
      return {
        name: x.name.replace('_', ', '),
        id: x.name,
        img: x.images.small,
        dateRange: contextDates[x.name || ''],
        isActive: params.value.contextName === x.name,
      };
    });
  });

  const onSwitchContext = async (context: string) => {
    const allContexts = getSolutionContexts();
    const selectedContext = allContexts.find((x) => x.name === context);
    if (!selectedContext) {
      return;
    }
    await updateSolutionImg(selectedContext.images.medium);
    await updateSolutionName(context.replace('_', ', '));
    const router = useRouter();
    const params = useParams();
    router.push({
      params: {
        ...params.value,
        contextName: context,
      },
    });
  };

  const diningActivities = () => {
    return getDiningActivities();
  };

  const experienceActivities = () => {
    return getExperienceActivities();
  };

  const availableRecommendations = computed(() => {
    return getAllRecommendationsContent();
  });

  const additionalPois = computed(() => {
    return AdditionalPoisModule.getPois();
  });

  const selectedPoiId = computed(() => {
    const params = useParams();
    return params.value.poiId || '';
  });

  const activityForSelectedPoiId = computed<Activity | null>(() => {
    return solution.value?.activities.find((x) => x.source?.content?.id === selectedPoiId.value) || null;
  });

  const activeSolutionFilters = computed(() => {
    return availableSolutionsInterestTags.value.reduce((r: Record<string, boolean>, x) => {
      r[x.name.toLowerCase()] = x.value;
      return r;
    }, {});
  });

  const sourceAlternatives = computed(() => {
    return getPoiAlternatives(selectedPoiId.value);
  });

  // find POI by selected activity
  const getPoiForSelectedItem = computed<CandidatePoiItem | null>(() => {
    // STEP 1: Look if we have activity in solution.activities, and return its source.
    if (activityForSelectedPoiId.value?.source?.content) {
      return activityForSelectedPoiId.value.source.content as CandidatePoiItem;
    }

    // can't search further if solution is not loaded
    if (!solution.value) {
      return null;
    }

    // STEP 2: Look if we have poi in solution recommendations
    const poiFromRecommendations = availableRecommendations.value.find((poi) => poi.id === selectedPoiId.value);
    if (poiFromRecommendations) {
      return poiFromRecommendations;
    }

    // STEP 3: Look if we have loaded poi before in additionapPois
    const poiFromAdditionalPois = additionalPois.value.find((poi) => poi.id === selectedPoiId.value);
    if (poiFromAdditionalPois) {
      return poiFromAdditionalPois;
    }

    // STEP 4: Look if we have loaded poi before in poiAlternatives
    const poiFromAlternatives = sourceAlternatives.value?.response?.alternatives?.find(
      (x) => x.source.candidate_id === selectedPoiId.value,
    );
    if (poiFromAlternatives && poiFromAlternatives.source.content) {
      return poiFromAlternatives.source.content as CandidatePoiItem;
    }

    return null;
  });

  return {
    selectedPoiId,
    activityForSelectedPoiId,
    availableRecommendations,
    activeSolutionFilters,
    getPoiForSelectedItem,
    ...restOfHeader,
    isLoading,
    solutionImg,
    destinationName,
    solutionCost,
    price,
    currency,
    removedItems,
    onRemoveFromTheList,
    solution,
    openHighlightDetails,
    openRecommendations,
    onShowPoiDetailsFromMap,
    timeFrameFilter,
    onFilterClick,
    travelersFilter,
    solutionTimeFrameFilterValue,
    flights,
    flightDescr,
    flightCaption,
    flightPriceDescription,
    stayActivity,
    stayName,
    stayPrice,
    flightPrice,
    experincesCaption,
    backToSummaryView,
    solutionDatesRange,
    getFlightName,
    isFlightAvailable,
    flightExplanations,
    isFlightInThePast,
    isOneLocation,
    turnOffFlight,
    turnOffStay,
    isFlightsOn,
    isStaysOn,
    selectedTripOption,
    flightsImg,
    availableTripOptions,
    activeTheme,
    availableContexts,
    onSwitchContext,
    pdfDestinationName,
    allStayActivities,
    getActivityPrice,
    diningActivities,
    experienceActivities,
    sourceAlternatives,
  };
}
